import { useState } from "react";
import {
  Box,
  Input,
  Button,
  Text,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  useMediaQuery,
} from "@chakra-ui/react";
import { validateCoupon } from "../../../../../http";

interface CouponResponse {
  isValid: boolean;
  discountType: "flat" | "percentage";
  discountWorth: number;
  maximumDiscount: number;
  redemptionType: "instant" | "cashback" | "already_discounted" | "single_use" | "limited_cycles" | "forever";
  offerId: string;
}

interface CouponValidatorProps {
  onCouponValidated: (couponData: any) => void;
}

export const CouponValidator: React.FC<CouponValidatorProps> = ({ onCouponValidated }) => {
  const [isMobile] = useMediaQuery("(max-width: 480px)");
  const [offerName, setOfferName] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [couponData, setCouponData] = useState<CouponResponse | null>(null);
  const [error, setError] = useState("");

  const handleValidate = async () => {
    if (!offerName.trim()) return;
    
    setIsLoading(true);
    setError("");
    setCouponData(null);
    
    try {
      const response = await validateCoupon({ offerName: offerName.trim(), category: "discount" });
      setCouponData(response.data);
      onCouponValidated(response.data);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || "Validation failed";
      setError(errorMsg);
      onCouponValidated(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setOfferName("");
    setCouponData(null);
    setError("");
    onCouponValidated(null);
  };

  const handleInputChange = (value: string) => {
    setOfferName(value);
    if (couponData || error) {
      setCouponData(null);
      setError("");
      onCouponValidated(null);
    }
  };

  return (
    <Box mb="16px">
      <Text fontSize={isMobile ? "14px" : "16px"} fontWeight="500" mb="8px">
        Have a Coupon?
      </Text>
      
      <VStack spacing="8px" align="stretch">
        <HStack spacing="8px">
          <Input
            placeholder="Enter offer name"
            value={offerName}
            onChange={(e) => handleInputChange(e.target.value)}
            fontSize={isMobile ? "12px" : "14px"}
            height={isMobile ? "32px" : "36px"}
            isDisabled={!!couponData}
            flex="1"
          />
          {couponData ? (
            <Button 
              size="sm" 
              onClick={handleReset} 
              colorScheme="red"
              height={isMobile ? "32px" : "36px"}
              px="12px"
            >
              Remove
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleValidate}
              isLoading={isLoading}
              isDisabled={!offerName.trim()}
              colorScheme="teal"
              height={isMobile ? "32px" : "36px"}
              px="12px"
            >
              Apply
            </Button>
          )}
        </HStack>

        {couponData && (
          <Alert status="success" borderRadius="4px" py="8px">
            <AlertIcon boxSize="12px" />
            <Box fontSize="11px" ml="4px">
              <Text fontWeight="bold">
                {couponData.discountType === "percentage" 
                  ? `${couponData.discountWorth}% OFF` 
                  : `₹${couponData.discountWorth} OFF`}
              </Text>

            </Box>
          </Alert>
        )}

        {error && (
          <Alert status="error" borderRadius="4px" py="8px">
            <AlertIcon boxSize="12px" />
            <Text fontSize="11px" ml="4px">{error}</Text>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};